import { type BaseTicketComment } from "@mg/schemas/src/christo/catalyst";
import { ArrowsOutSimple, CircleNotch, X } from "@phosphor-icons/react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  type ImperativePanelGroupHandle,
  Panel,
  PanelGroup,
} from "react-resizable-panels";

import {
  AssetViewerPane,
  type AssetFile,
  type AssetViewerPaneHandle,
} from "./AssetViewerPane";
import { ChevronNavButton } from "./ChevronNavButton";
import { ResizeHandle } from "./ResizeHandle";

import { findContextFileIndex } from "../chatUtils";
import { usePersistedLayout } from "../hooks/usePersistedLayout";
import { useTicketViewData } from "../hooks/useTicketView";

const CITATION_HIGHLIGHT_DURATION_MS = 5000;

type PendingCitation = {
  pageNumber: number;
  position?: { x: number; y: number };
  sourceUrl?: string;
  originalURL?: string;
};

interface CampaignReviewPaneProps {
  /** The review ticket to display (artwork + AI comments + context). */
  ticketId: string;
  /** Close the review screen and return to the default project view. */
  onClose: () => void;
}

/**
 * The project review screen: artwork in the center with comments docked below
 * the asset, and context/reference documents in a dedicated right column.
 *
 * Selection state (active comment + citation highlight) mirrors
 * `TicketViewPane`, but the layout is intentionally different: the project
 * workspace keeps the feed/chat visible on the left, so this pane owns the
 * artwork + comments stack and the context viewer.
 *
 * Artwork and context use a stable PanelGroup whenever the context column is
 * expanded so users can resize independently. Collapsing context or opening a
 * review without context assets falls back to a single artwork column — no
 * width threshold toggle, so AssetViewerPane never remounts from layout churn.
 */
export function CampaignReviewPane({
  ticketId,
  onClose,
}: CampaignReviewPaneProps) {
  const {
    comments,
    isLoading,
    hasContextAssets,
    contextFiles,
    latestReviewFiles,
  } = useTicketViewData(ticketId);

  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(
    null,
  );
  const [highlightPing, setHighlightPing] = useState<{
    x: number;
    y: number;
    page?: number;
  } | null>(null);
  const [contextHighlight, setContextHighlight] = useState<{
    x: number;
    y: number;
    page?: number;
  } | null>(null);
  const focusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contextHighlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const pendingCitationRef = useRef<PendingCitation | null>(null);
  const contextPaneRef = useRef<AssetViewerPaneHandle>(null);
  const [artworkHeaderControls, setArtworkHeaderControls] =
    useState<ReactNode | null>(null);
  const [contextHeaderControls, setContextHeaderControls] =
    useState<ReactNode | null>(null);
  const [contextCollapsed, setContextCollapsed] = useState(false);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const panelGroupRef = useRef<ImperativePanelGroupHandle>(null);
  const ARTWORK_CONTEXT_DEFAULT: number[] = [62, 38];
  const ARTWORK_CONTEXT_MINS: number[] = [35, 22];
  const artworkContextLayout = usePersistedLayout({
    storageKey: "askpuntt-project-review-artwork-context",
    defaultLayout: ARTWORK_CONTEXT_DEFAULT,
    minSizes: ARTWORK_CONTEXT_MINS,
  });
  const dragAbortSignal = `${ticketId}:${contextCollapsed ? "collapsed" : "expanded"}`;
  const dragInProgress = activeDragId !== null;
  const useResizableContextLayout = hasContextAssets && !contextCollapsed;

  // Defense-in-depth: never render pins for deleted comments even if a stale
  // one slips through a poll race (mirrors TicketViewPane).
  const activeComments = useMemo(
    () => (comments ?? []).filter((c) => !c.isDeleted),
    [comments],
  );

  // Stable asset identity so AssetViewerPane's memo doesn't thrash on polls.
  const assets: AssetFile[] = useMemo(
    () => latestReviewFiles,
    [latestReviewFiles],
  );

  // Reset selection when switching to a different ticket. Declared BEFORE the
  // auto-select effect so that on mount the null-reset runs first and the
  // auto-select below still lands on the first comment (effect order matters).
  useEffect(() => {
    setSelectedCommentId(null);
    setHighlightPing(null);
    setContextHighlight(null);
    pendingCitationRef.current = null;
    setContextCollapsed(false);
  }, [ticketId]);

  // Auto-select the first comment so the artwork lands on a pin and the
  // comments strip shows real content immediately.
  useEffect(() => {
    if (!selectedCommentId && activeComments.length) {
      setSelectedCommentId(activeComments[0]._id ?? null);
    }
  }, [selectedCommentId, activeComments]);

  useEffect(
    () => () => {
      if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
      if (contextHighlightTimerRef.current) {
        clearTimeout(contextHighlightTimerRef.current);
      }
    },
    [],
  );

  const applyContextCitation = useCallback(
    ({ pageNumber, position, sourceUrl, originalURL }: PendingCitation) => {
      const targetIdx = findContextFileIndex(
        contextFiles,
        sourceUrl,
        originalURL,
      );
      if (targetIdx >= 0) {
        contextPaneRef.current?.navigateToFile(targetIdx, pageNumber);
      } else {
        contextPaneRef.current?.navigateToPage(pageNumber);
      }
      if (position) {
        if (contextHighlightTimerRef.current) {
          clearTimeout(contextHighlightTimerRef.current);
        }
        setContextHighlight(null);
        requestAnimationFrame(() => {
          setContextHighlight({
            x: position.x,
            y: position.y,
            page: pageNumber,
          });
          contextHighlightTimerRef.current = setTimeout(
            () => setContextHighlight(null),
            CITATION_HIGHLIGHT_DURATION_MS,
          );
        });
      }
    },
    [contextFiles],
  );

  // When context was collapsed, expand first then navigate once the viewer mounts.
  useEffect(() => {
    if (!pendingCitationRef.current) return;
    if (!hasContextAssets || contextCollapsed) return;
    const pending = pendingCitationRef.current;
    pendingCitationRef.current = null;
    applyContextCitation(pending);
  }, [hasContextAssets, contextCollapsed, applyContextCitation]);

  const handleCommentClick = useCallback((comment: BaseTicketComment) => {
    setSelectedCommentId(comment._id ?? null);
    if (comment.rel_x != null && comment.rel_y != null) {
      if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
      setHighlightPing({
        x: comment.rel_x,
        y: comment.rel_y,
        page: comment.page_num,
      });
      focusTimerRef.current = setTimeout(() => setHighlightPing(null), 3000);
    }
  }, []);

  // Citation links switch the context file, jump to the page, and ping the
  // source coordinates — same routing as standalone Ask Puntt chat.
  const handleCitationClick = useCallback(
    (
      pageNumber: number,
      position?: { x: number; y: number },
      sourceUrl?: string,
      originalURL?: string,
    ) => {
      const citation: PendingCitation = {
        pageNumber,
        position,
        sourceUrl,
        originalURL,
      };
      if (hasContextAssets && contextCollapsed) {
        pendingCitationRef.current = citation;
        setContextCollapsed(false);
        return;
      }
      applyContextCitation(citation);
    },
    [applyContextCitation, contextCollapsed, hasContextAssets],
  );

  const hideArtworkCommentPanel = activeComments.length === 0;

  const artworkColumnInner = (
    <>
      <div
        className="flex h-12 min-w-0 shrink-0 items-center gap-2 border-b border-puntt-neutral-gray-5 px-4"
        data-testid="campaign-review-artwork-header"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close review"
          data-testid="campaign-review-close"
          className="flex size-8 shrink-0 items-center justify-center rounded-md text-puntt-neutral-gray-11 transition-colors hover:bg-puntt-neutral-gray-3 hover:text-puntt-neutral-gray-12"
        >
          <X size={18} aria-hidden="true" />
        </button>
        <h3 className="shrink-0 text-[11px] font-bold uppercase tracking-wide text-puntt-neutral-gray-11">
          Artwork
        </h3>
        <div
          className="ml-auto flex shrink-0 items-center gap-2"
          data-testid="campaign-review-artwork-header-controls"
        >
          {artworkHeaderControls}
        </div>
      </div>
      <div
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
        data-testid="campaign-review-artwork-body"
      >
        {isLoading ? (
          <div
            className="flex flex-1 items-center justify-center"
            data-testid="campaign-review-loading"
          >
            <CircleNotch
              size={24}
              weight="bold"
              className="animate-spin text-puntt-neutral-gray-9"
            />
          </div>
        ) : (
          <>
            <div className="min-h-0 flex-1">
              <AssetViewerPane
                key={ticketId}
                title=""
                assets={assets}
                comments={activeComments}
                activeCommentId={selectedCommentId}
                onCommentClick={handleCommentClick}
                onCitationClick={handleCitationClick}
                onPinClick={handleCommentClick}
                highlightPosition={highlightPing}
                ticketId={ticketId}
                hideCommentPanel={hideArtworkCommentPanel}
                viewerToolbarPlacement="external"
                onHeaderControlsChange={setArtworkHeaderControls}
                commentComposePlacement="inline"
              />
            </div>
            {hideArtworkCommentPanel && (
              <p
                className="shrink-0 border-t border-puntt-neutral-gray-5 p-4 text-[13px] leading-relaxed text-puntt-neutral-gray-11"
                data-testid="campaign-review-comments-empty"
              >
                No comments on this review yet.
              </p>
            )}
          </>
        )}
      </div>
    </>
  );

  const contextColumnInner = (
    <>
      <div
        className="flex h-12 min-w-0 shrink-0 items-center gap-2 border-b border-puntt-neutral-gray-5 px-4"
        data-testid="campaign-review-context-header"
      >
        <h3 className="shrink-0 text-[11px] font-bold uppercase tracking-wide text-puntt-neutral-gray-11">
          Context
        </h3>
        <div
          className="ml-auto flex shrink-0 items-center gap-2"
          data-testid="campaign-review-context-header-controls"
        >
          {contextHeaderControls}
          <ChevronNavButton
            direction="right"
            onClick={() => setContextCollapsed(true)}
            aria-label="Collapse context"
            data-testid="campaign-review-context-collapse"
          />
        </div>
      </div>
      <div
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
        data-testid="campaign-review-context-body"
      >
        <AssetViewerPane
          ref={contextPaneRef}
          key={`${ticketId}-context`}
          title=""
          assets={contextFiles}
          hideCommentPanel
          highlightPosition={contextHighlight}
          viewerToolbarPlacement="external"
          viewerContentInset="edge"
          onHeaderControlsChange={setContextHeaderControls}
        />
      </div>
    </>
  );

  return (
    <section
      className="flex h-full min-w-0 flex-1 overflow-hidden"
      data-testid="campaign-review-pane"
      aria-label="Asset review"
    >
      {useResizableContextLayout ? (
        <PanelGroup
          direction="horizontal"
          ref={panelGroupRef}
          onLayout={artworkContextLayout.onLayout}
          className={`flex flex-1 ${dragInProgress ? "[&_*]:!transition-none" : ""}`}
        >
          <Panel
            id="artwork"
            order={0}
            minSize={ARTWORK_CONTEXT_MINS[0]}
            defaultSize={artworkContextLayout.layout[0]}
          >
            <div
              className="flex h-full min-w-0 flex-col overflow-hidden bg-base-white"
              data-testid="pane-artwork"
            >
              {artworkColumnInner}
            </div>
          </Panel>
          <ResizeHandle
            id="context-artwork"
            panelGroupRef={panelGroupRef}
            defaultLayout={ARTWORK_CONTEXT_DEFAULT}
            abortSignal={dragAbortSignal}
            onDraggingChange={(d) =>
              setActiveDragId(d ? "context-artwork" : null)
            }
          />
          <Panel
            id="context"
            order={1}
            minSize={ARTWORK_CONTEXT_MINS[1]}
            defaultSize={artworkContextLayout.layout[1]}
          >
            <aside
              className="flex size-full min-w-0 flex-col overflow-hidden border-l border-puntt-neutral-gray-5 bg-base-white"
              data-testid="pane-context"
              aria-label="Context"
              role="region"
            >
              {contextColumnInner}
            </aside>
          </Panel>
        </PanelGroup>
      ) : (
        <>
          <div
            className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-base-white"
            data-testid="campaign-review-artwork-column"
          >
            {artworkColumnInner}
          </div>

          {hasContextAssets && contextCollapsed && (
            <button
              type="button"
              onClick={() => setContextCollapsed(false)}
              aria-label="Expand context"
              aria-expanded={false}
              data-testid="campaign-review-context-expand"
              className="flex w-8 shrink-0 flex-col items-center justify-start gap-2 border-l border-puntt-neutral-gray-5 bg-puntt-neutral-gray-1 py-3 text-puntt-neutral-gray-11 transition-colors hover:bg-puntt-neutral-gray-3 hover:text-puntt-neutral-gray-12"
            >
              <ArrowsOutSimple size={14} aria-hidden="true" />
              <span className="text-[11px] font-bold uppercase tracking-wide [writing-mode:vertical-rl]">
                Context
              </span>
            </button>
          )}
        </>
      )}
    </section>
  );
}
